// بيانات الطفل غير المسجّل — تُعرض داخل نافذة الاشتراك بعد موافقة الموظف.
// الحقول هي ما يطلبه العميل بالضبط: اسم الطفل (مُعبَّأ مسبقاً بما كُتب في
// البحث وقابل للتعديل) واسم ولي الأمر ورقم هاتفه وعمر الطفل، والصورة اختيارية.
// «الجنس» معروض لأن سجل الطفل في النظام يلزمه — وافتراضه «ذكر» فلا يوقف البيع.
import { useRef } from 'react';
import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { Avatar } from '../../../layout/Avatar.jsx';

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

export function NewChildFields({ draft, onChange, errors = {} }) {
  const fileRef = useRef(null);
  const set = (key) => (e) => onChange({ ...draft, [key]: e.target.value });

  const pickPhoto = (file) => {
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      onChange({ ...draft, photo: null, photoError: 'حجم الصورة يتجاوز 5 ميغابايت' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange({ ...draft, photo: String(reader.result || ''), photoError: '' });
    reader.onerror = () => onChange({ ...draft, photo: null, photoError: 'تعذّر قراءة الصورة' });
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3 rounded-[10px] border border-line p-3" style={{ background: '#FFFDF8' }}>
      <div className="text-[12.5px] font-bold text-ink-2">بيانات الطفل الجديد</div>

      <Field label="اسم الطفل *" error={errors.childName}>
        <input className="input" value={draft.childName || ''} onChange={set('childName')} placeholder="الاسم الكامل" />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="اسم ولي الأمر *" error={errors.guardianName}>
          <input className="input" value={draft.guardianName || ''} onChange={set('guardianName')} placeholder="مثلاً: أبو أحمد" />
        </Field>
        <Field label="رقم هاتف ولي الأمر *" error={errors.guardianPhone}>
          <input
            className="input"
            value={draft.guardianPhone || ''}
            onChange={set('guardianPhone')}
            placeholder="07XXXXXXXXX"
            dir="ltr"
            style={{ textAlign: 'right', fontFamily: 'monospace' }}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field label="عمر الطفل *" error={errors.childAge}>
          <input
            className="input"
            type="number"
            min={0}
            max={18}
            value={draft.childAge ?? ''}
            onChange={set('childAge')}
            placeholder="بالسنوات"
            dir="ltr"
            style={{ textAlign: 'right' }}
          />
        </Field>
        <Field label="الجنس">
          <div className="flex gap-2">
            {[{ k: 'male', l: 'ذكر' }, { k: 'female', l: 'أنثى' }].map((g) => (
              <button
                key={g.k}
                type="button"
                onClick={() => onChange({ ...draft, childGender: g.k })}
                className={clsx(
                  'px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold border transition-colors flex-1',
                  (draft.childGender || 'male') === g.k
                    ? 'bg-orange text-white border-orange'
                    : 'bg-white text-ink-2 border-line hover:bg-paper-2'
                )}
              >
                {g.l}
              </button>
            ))}
          </div>
        </Field>
      </div>

      <div>
        <label className="block text-xs font-semibold text-ink-2 mb-1">صورة الطفل (اختياري)</label>
        <div className="flex items-center gap-2">
          <Avatar name={draft.childName || '؟'} id="new-child" src={draft.photo || null} size="sm" />
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => pickPhoto(e.target.files && e.target.files[0])}
          />
          <button type="button" className="btn" style={{ padding: '6px 10px', fontSize: 12 }} onClick={() => fileRef.current?.click()}>
            <Icon name="camera" size={13} /> {draft.photo ? 'تغيير الصورة' : 'اختيار صورة'}
          </button>
          {draft.photo ? (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ padding: '6px 8px', fontSize: 12 }}
              onClick={() => { onChange({ ...draft, photo: null, photoError: '' }); if (fileRef.current) fileRef.current.value = ''; }}
            >
              <Icon name="trash" size={13} /> إزالة
            </button>
          ) : null}
        </div>
        {draft.photoError ? <div className="text-[11.5px] text-red mt-1">{draft.photoError}</div> : null}
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-ink-2 mb-1">{label}</label>
      {children}
      {error ? <div className="text-[11.5px] text-red mt-1">{error}</div> : null}
    </div>
  );
}

/** فحص بيانات الطفل الجديد قبل الإرسال — نفس قواعد الخادم. */
export function validateNewChild(draft) {
  const errors = {};
  const name = String(draft.childName || '').trim();
  if (name.length < 2) errors.childName = 'اسم الطفل مطلوب';
  if (String(draft.guardianName || '').trim().length < 2) errors.guardianName = 'اسم ولي الأمر مطلوب';
  if (!/^07[0-9]{9}$/.test(String(draft.guardianPhone || '').trim())) {
    errors.guardianPhone = 'رقم الهاتف يجب أن يبدأ بـ 07 ويتكون من 11 رقماً';
  }
  const age = Number(draft.childAge);
  if (draft.childAge === '' || draft.childAge == null || !Number.isFinite(age)) {
    errors.childAge = 'عمر الطفل مطلوب';
  } else if (age < 0 || age > 18) {
    errors.childAge = 'العمر بين 0 و 18';
  }
  return errors;
}
